import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

let initialised = false;
function ensureInit() {
  if (initialised) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'loose',
    fontFamily: 'inherit',
    flowchart: { htmlLabels: true, curve: 'basis' },
    sequence: { showSequenceNumbers: false },
  });
  initialised = true;
}

interface MermaidDiagramProps {
  chart: string;
}

export function MermaidDiagram({ chart }: MermaidDiagramProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string>('');

  useEffect(() => {
    ensureInit();
    let cancelled = false;
    const id = `m-${Math.random().toString(36).slice(2, 10)}`;
    mermaid
      .render(id, chart)
      .then(({ svg }) => {
        if (!cancelled) {
          setSvg(svg);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSvg('');
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 p-3">
        <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">Mermaid render error</p>
        <pre className="text-[11px] text-red-600 dark:text-red-300 whitespace-pre-wrap">{error}</pre>
        <details className="mt-2">
          <summary className="text-[11px] text-red-700 dark:text-red-400 cursor-pointer">Show source</summary>
          <pre className="mt-1 text-[11px] text-red-600 dark:text-red-300 whitespace-pre-wrap">{chart}</pre>
        </details>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="my-4 rounded-lg border border-gray-200 dark:border-dark-300/40 bg-white dark:bg-dark-300/10 p-4 overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
